//  * @param {number|string} value - The number to format.
//  * @param {string} currencySymbol - Currency symbol to prepend (default: '$').
//  * @returns {string} Formatted currency string.
//  */

export function formatCurrency(value, currencySymbol = "") {
  if (typeof value === "string" && value.trim().endsWith("-")) {
    value = "-" + value.trim().slice(0, -1); // Move '-' to front
  }

  const number = Number(value);
  if (isNaN(number)) return `${currencySymbol}0.00`;

  return `${currencySymbol}${number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Utility functions for dispute data processing
export const calculateDisputeStats = (disputes, userRole = "customer") => {
  if (!disputes || !Array.isArray(disputes)) {
    return getEmptyStats(userRole);
  }

  if (userRole === "customer") {
    return calculateCustomerStats(disputes);
  } else {
    return calculateResolverStats(disputes);
  }
};

const getEmptyStats = (userRole) => {
  const baseStats = {
    total: 0,
    byCategory: {},
    byStatus: {},
  };

  if (userRole === "customer") {
    return {
      ...baseStats,
      pending: 0,
      resolved: 0,
      inProgress: 0,
      needMoreInfo: 0,
      escalated: 0,
    };
  } else {
    return {
      ...baseStats,
      pending: 0,
      resolved: 0,
      inProgress: 0,
      needMoreInfo: 0,
      escalated: 0,
      byApproverStatus: {},
    };
  }
};

const calculateCustomerStats = (disputes) => {
  const stats = {
    total: disputes.length,
    pending: disputes.filter((dispute) => dispute.disputeStatus === "Pending")
      .length,
    resolved: disputes.filter((dispute) => dispute.disputeStatus === "Resolved")
      .length,
    inProgress: disputes.filter(
      (dispute) => dispute.disputeStatus === "In Progress"
    ).length,
    needMoreInfo: disputes.filter(
      (dispute) => dispute.disputeStatus === "Need More Info"
    ).length,
    escalated: disputes.filter(
      (dispute) => dispute.approver_status === "Escalated" || dispute?.disputeStatus === "Escalated"
    ).length,
    byCategory: {},
    byStatus: {},
  };

  // Calculate disputes by category and status
  disputes.forEach((dispute) => {
    const category = dispute.disputeCategoryName || "Unknown";
    const status = dispute.disputeStatus || "Unknown";

    stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
    stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
  });

  return stats;
};

const calculateResolverStats = (disputes) => {
  const stats = {
    total: disputes.length,
    pending: disputes.filter((dispute) => dispute.disputeStatus === "Pending" && dispute.approver_status !== "Escalated")
      .length,
    resolved: disputes.filter((dispute) => dispute.disputeStatus === "Resolved" && dispute.approver_status !== "Escalated")
      .length,
    inProgress: disputes.filter(
      (dispute) => dispute.disputeStatus === "In Progress" && dispute.approver_status !== "Escalated"
    ).length,
    needMoreInfo: disputes.filter(
      (dispute) => dispute.disputeStatus === "Need More Info" && dispute.approver_status !== "Escalated"
    ).length,
    escalated: disputes.filter(
      (dispute) => dispute.approver_status === "Escalated"
    ).length,
    byCategory: {},
    byStatus: {},
    byApproverStatus: {},
  };

  // Calculate disputes by category, status, and approver status
  disputes.forEach((dispute) => {
    const category = dispute.disputeCategoryName || "Unknown";
    const status = dispute.disputeStatus || "Unknown";
    const approverStatus = dispute.approver_status || "Not Set";

    stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
    stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
    stats.byApproverStatus[approverStatus] =
      (stats.byApproverStatus[approverStatus] || 0) + 1;
  });

  return stats;
};

export const getDisputeCardsData = (disputes, userRole = "customer", t) => {
  const stats = calculateDisputeStats(disputes, userRole);

  if (userRole === "customer") {
    return getCustomerCards(stats, t);
  } else {
    return getResolverCards(stats, t);
  }
};

const getCustomerCards = (stats, t) => [
  
  {
    id: "total-disputes",
    title: (t("analytics.TotalDisputes")),
    value: stats.total,
    icon: "total",
    colorScheme: "primary",
    trendDirection: "up",
    subtitle: (t("analytics.Alldisputes")),
    size: "tiny",
  },
  {
    id: "pending-disputes",
    title: (t("analytics.Pending")),
    value: stats.pending,
    icon: "pending",
    colorScheme: "warning",
    trendDirection: "down",
    subtitle: (t("analytics.Awaitingresolution")),
    size: "tiny",
  },
  {
    id: "in-progress-disputes",
    title: (t("analytics.InProgress")),
    value: stats.inProgress,
    icon: "activity",
    colorScheme: "info",
    trendDirection: "up",
    subtitle: (t("analytics.Currentlybeingworkedon")),
    size: "tiny",
  },
  {
    id: "need-more-info-disputes",
    title: (t("analytics.NeedMoreInfo")),
    value: stats.needMoreInfo,
    icon: "info",
    colorScheme: "secondary",
    trendDirection: "neutral",
    subtitle: (t("analytics.Requiresadditionalinformation")),
    size: "tiny",
  },
  {
    id: "escalated-disputes",
    title: (t("analytics.Escalated")),
    value: stats.escalated,
    icon: "escalate",
    colorScheme: "danger",
    trendDirection: "up",
    subtitle: (t("analytics.Requireshigherapproval")),
    size: "tiny",
  },
  {
    id: "resolved-disputes",
    title: (t("analytics.Resolved")),
    value: stats.resolved,
    icon: "resolved",
    colorScheme: "success",
    trendDirection: "up",
    subtitle: (t("analytics.Successfullyclosed")),
    size: "tiny",
  },
];

const getResolverCards = (stats, t) => [
  {
    id: "total-disputes",
    title: (t("analytics.TotalDisputes")),
    value: stats.total,
    icon: "total",
    colorScheme: "primary",
    trendDirection: "up",
    subtitle: (t("analytics.Alldisputestoreview")),
    size: "tiny",
  },
  {
    id: "pending-disputes",
    title: (t("analytics.PendingReview")),
    value: stats.pending,
    icon: "pending",
    colorScheme: "warning",
    trendDirection: "down",
    subtitle: (t("analytics.Awaitinginitialreview")),
    size: "tiny",
  },
  {
    id: "in-progress-disputes",
    title: (t("analytics.InProgress")),
    value: stats.inProgress,
    icon: "activity",
    colorScheme: "info",
    trendDirection: "up",
    subtitle: (t("analytics.Currentlybeingprocessed")),
    size: "tiny",
  },
  {
    id: "need-more-info-disputes",
    title: (t("analytics.NeedMoreInfo")),
    value: stats.needMoreInfo,
    icon: "info",
    colorScheme: "secondary",
    trendDirection: "neutral",
    subtitle: (t("analytics.Requiresadditionalinformation")),
    size: "tiny",
  },
  {
    id: "escalated-disputes",
    title: (t("analytics.Escalated")),
    value: stats.escalated,
    icon: "escalate",
    colorScheme: "danger",
    trendDirection: "up",
    subtitle: (t("analytics.Requireshigherapproval")),
    size: "tiny",
  },
  {
    id: "resolved-disputes",
    title: (t("analytics.Resolved")),
    value: stats.resolved,
    icon: "resolved",
    colorScheme: "success",
    trendDirection: "up",
    subtitle: (t("analytics.Successfullyresolved")),
    size: "tiny",
  },
];

// Additional utility functions for resolvers
export const getEscalatedDisputes = (disputes) => {
  if (!disputes) return [];
  return disputes.filter((dispute) => dispute.approver_status === "Escalated");
};

export const getUnassignedDisputes = (disputes) => {
  if (!disputes) return [];
  return disputes.filter(
    (dispute) => !dispute.assignedTo || dispute.assignedTo === "Unassigned"
  );
};

export const getDisputesByApproverStatus = (disputes, approverStatus) => {
  if (!disputes) return [];
  return disputes.filter(
    (dispute) => dispute.approver_status === approverStatus
  );
};

export const getDisputesRequiringAction = (disputes) => {
  if (!disputes) return [];
  return disputes.filter(
    (dispute) =>
      dispute.disputeStatus === "Pending" &&
      dispute.approver_status !== "Resolved"
  );
};

// Existing utility functions
export const filterDisputesByStatus = (disputes, status) => {
  if (!disputes) return [];
  return disputes.filter((dispute) => dispute.disputeStatus === status);
};

export const filterDisputesByCategory = (disputes, categoryId) => {
  if (!disputes) return [];
  return disputes.filter((dispute) => dispute.disputeCategoryId === categoryId);
};

export const getDisputesAssignedToUser = (disputes, username) => {
  if (!disputes) return [];
  return disputes.filter((dispute) => dispute.assignedTo === username);
};

export const getRecentDisputes = (disputes, days = 7) => {
  if (!disputes) return [];
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);

  return disputes.filter((dispute) => {
    const createdAt = new Date(dispute.createdAt);
    return createdAt >= cutoffDate;
  });
};

// Formatters for customer dashboard analytics cards

// Chart colors
export const CHART_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
];

// Data processing functions
export const processInvoiceData = (invoices, creditNotes) => {
  const invoicesByMonth = {};
  const creditNotesByMonth = {};

  invoices.forEach((invoice) => {
    const date = new Date(parseInt(invoice.InvoiceDate.match(/\d+/)[0]));
    const monthKey = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}`;

    if (!invoicesByMonth[monthKey]) {
      invoicesByMonth[monthKey] = { count: 0, amount: 0 };
    }
    invoicesByMonth[monthKey].count += 1;
    invoicesByMonth[monthKey].amount +=
      parseFloat(invoice.TotalInv.replace(/\s+/g, "")) || 0;
  });

  creditNotes.forEach((note) => {
    const date = note.CreditNoteDate;
    const monthKey = `${date.substring(0, 4)}-${date.substring(4, 6)}`;

    if (!creditNotesByMonth[monthKey]) {
      creditNotesByMonth[monthKey] = { count: 0, amount: 0 };
    }
    creditNotesByMonth[monthKey].count += 1;
    creditNotesByMonth[monthKey].amount +=
      parseFloat(note.TotalcreditnoteAmount) || 0;
  });

  // Combine data for chart
  const allMonths = new Set([
    ...Object.keys(invoicesByMonth),
    ...Object.keys(creditNotesByMonth),
  ]);
  const chartData = Array.from(allMonths)
    .sort()
    .map((month) => ({
      month: month,
      invoices: invoicesByMonth[month]?.amount || 0,
      creditNotes: creditNotesByMonth[month]?.amount || 0,
      invoiceCount: invoicesByMonth[month]?.count || 0,
      creditNoteCount: creditNotesByMonth[month]?.count || 0,
    }));

  return chartData;
};

export const processPaymentData = (payments) => {
  const paymentsByMonth = {};

  payments.forEach((payment) => {
    const date = new Date(parseInt(payment.PayDate.match(/\d+/)[0]));
    const monthKey = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}`;

    if (!paymentsByMonth[monthKey]) {
      paymentsByMonth[monthKey] = { count: 0, amount: 0 };
    }
    paymentsByMonth[monthKey].count += 1;
    paymentsByMonth[monthKey].amount += parseFloat(payment.PayAmt) || 0;
  });

  return Object.entries(paymentsByMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, data]) => ({
      month,
      payments: data.amount,
      count: data.count,
    }));
};

export const processLogisticsData = (logistics) => {
  const statusCounts = logistics.reduce((acc, item) => {
    const status = item.Status || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const transportTypes = logistics.reduce((acc, item) => {
    const type = item.TypeOfTransport || "Unknown";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  return {
    statusData: Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
    })),
    transportData: Object.entries(transportTypes).map(([name, value]) => ({
      name,
      value,
    })),
  };
};

export const processDisputeData = (disputes) => {
  const statusCounts = disputes.reduce((acc, dispute) => {
    const status = dispute.disputeStatus || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const categoryData = disputes.reduce((acc, dispute) => {
    const category = dispute.disputeCategoryName || "Unknown";
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  return {
    statusData: Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
    })),
    categoryData: Object.entries(categoryData).map(([name, value]) => ({
      name,
      value,
    })),
  };
};
